import React from 'react';
import type { DefaultTheme } from '@mui/styles/defaultTheme';
import { Box, Divider, MenuItem } from '@mui/material';
import { makeStyles } from '@mui/styles';
import classnames from 'classnames';

import DatePicker from '../../../console/agencies/quotes/PrimeX/customizeQuote/components/DatePicker';
import { checkDateBounds } from '../../../utils/dateInput.utils';
import type { RetroDateValue, RetroPreset } from './retroDateField.types';

const OUT_OF_RANGE_DAY_CLASS = 'retro-date-day--out-of-range';

export type RetroDatePopoverBodyProps = {
  readonly selected: Date | null;
  readonly presets: readonly RetroPreset[];
  readonly selectedValue: RetroDateValue;
  readonly onSelectDate: (date: Date) => void;
  readonly onSelectPreset: (preset: RetroPreset) => void;
  /** Month to open on when the committed value is a period, not a date. */
  readonly defaultDate?: Date;
  readonly minDate?: Date;
  readonly maxDate?: Date;
};

/**
 * The contents of the retro-date popover: a calendar the user can jump around
 * by month and year, beside the retroactive period shortcuts.
 *
 * The month and year dropdowns are what keep an old retro date two clicks away
 * rather than one back-arrow press per month.
 */
export const RetroDatePopoverBody = ({
  selected,
  presets,
  selectedValue,
  onSelectDate,
  onSelectPreset,
  defaultDate,
  minDate,
  maxDate,
}: RetroDatePopoverBodyProps) => {
  const classes = useStyles();

  const markOutOfRangeDays = (date: Date) => {
    return checkDateBounds(date, minDate, maxDate) === 'ok'
      ? ''
      : OUT_OF_RANGE_DAY_CLASS;
  };

  return (
    <Box className={classnames(classes.root, classes.calendar)}>
      <DatePicker.Calendar
        selected={selected}
        openToDate={selected ?? defaultDate ?? maxDate}
        minDate={minDate}
        maxDate={maxDate}
        showMonthDropdown
        showYearDropdown
        dropdownMode="select"
        dayClassName={markOutOfRangeDays}
        onChange={(date) => date && onSelectDate(date as Date)}
      />

      {presets.length > 0 && (
        <Box className={classes.presets}>
          <Box className={classes.presetsHeading}>Retro Active Period</Box>
          <Divider className={classes.presetsDivider} />

          {presets.map((preset) => (
            <MenuItem
              key={preset.value}
              selected={preset.value === selectedValue}
              className={classes.presetItem}
              onClick={() => onSelectPreset(preset)}
            >
              {preset.label}
            </MenuItem>
          ))}
        </Box>
      )}
    </Box>
  );
};

const useStyles = makeStyles<DefaultTheme>(({ palette }) => ({
  root: {
    display: 'flex',
    gap: '1rem',
    padding: '1rem',
    backgroundColor: palette.background.active,
  },
  /**
   * react-datepicker renders its own month/year selects; align them with the
   * surrounding surface rather than leaving them browser-default.
   */
  calendar: {
    '& .react-datepicker__header__dropdown': {
      display: 'flex',
      gap: '0.35rem',
      justifyContent: 'center',
      padding: '0.35rem 0',
    },
    '& .react-datepicker__month-select, & .react-datepicker__year-select': {
      backgroundColor: palette.background.active,
      color: palette.text.contrastText,
      border: `1px solid ${palette.divider}`,
      borderRadius: 3,
      padding: '0.1rem 0.2rem',
    },
    [`& .${OUT_OF_RANGE_DAY_CLASS}`]: {
      textDecoration: 'line-through',
      opacity: 0.25,
      cursor: 'not-allowed',
    },
  },
  presets: {
    width: '10rem',
    borderLeft: `1px solid ${palette.divider}`,
    paddingLeft: '1rem',
  },
  presetsHeading: {
    fontSize: '1.1rem',
    fontWeight: 600,
    color: palette.text.contrastText,
  },
  presetsDivider: {
    opacity: 0.25,
    margin: '0.35rem 0 0.25rem',
  },
  presetItem: {
    transition: 'all 0.25s',
    padding: '0.15rem 0.25rem',
    borderRadius: 5,
    '&:hover': {
      paddingLeft: '0.5rem',
    },
  },
}));
